import { useEffect, useMemo, useState } from "react";
import amenitiesApi from "@/api/amenitiesApi";
import AmenityCatalog from "@/model/AmenityCatalog";
import AmenityCategoryResponse from "@/model/AmenityCategoryResponse";
import AmenityResponse from "@/model/AmenityResponse";

// The catalog is static: loaded once per page load and shared by every component.
let cached: AmenityCategoryResponse[] | null = null;
let pending: Promise<AmenityCategoryResponse[]> | null = null;

function loadCatalog(): Promise<AmenityCategoryResponse[]> {
  if (cached) {
    return Promise.resolve(cached);
  }
  if (!pending) {
    pending = amenitiesApi
      .listAmenities()
      .then((categories) => {
        if (!Array.isArray(categories)) {
          throw new Error("Unexpected amenity catalog");
        }
        cached = categories;
        return categories;
      })
      .finally(() => {
        // Only a successful answer is kept: a failure is retried by the next component.
        pending = null;
      });
  }
  return pending;
}

function useAmenityCatalog(): AmenityCatalog {
  const [categories, setCategories] = useState<AmenityCategoryResponse[]>(cached ?? []);
  const [isLoading, setIsLoading] = useState(cached === null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (cached) {
      return;
    }
    let cancelled = false;
    loadCatalog()
      .then((loaded) => {
        if (!cancelled) {
          setCategories(loaded);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => {
    const byCode = new Map<string, AmenityResponse>();
    categories.forEach((category) =>
      category.amenities.forEach((amenity) => byCode.set(amenity.code, amenity))
    );
    return {
      categories,
      find: (code: string) => byCode.get(code),
      labelOf: (code: string) => byCode.get(code)?.label ?? code,
      isLoading,
      error,
    };
  }, [categories, isLoading, error]);
}

export default useAmenityCatalog;
